import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getIssue } from "@services/account";
import { Ticket, TicketIssue, TicketStatus } from "@services/tickets/types";
import { IssueName } from "@services/account/types";
import { putTicket } from "@services/tickets";
import { Priority } from "@services/common/types";
import { useErrorNotification } from "@common/components/Notification";
import { FormContext } from "../context/formContext";
import Button from "@common/components/Button";
import CloseCaseContent from "./CloseCaseContent";
import {
  CloseCaseTicketActionContext,
  CloseCaseTicketContext,
  ErrorIssueActionContext,
} from "./context";
import { TicketListContext } from "@pages/Tickets/context";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";

interface CloseCaseDialogProps {
  open?: boolean;
  onClose?: (saved?: boolean) => void;
}

const CloseCaseDialog = (props: CloseCaseDialogProps) => {
  const { dispatchTicket, form, reset } = useContext(FormContext);
  const { dispatch } = useContext(TicketListContext);
  const { ticket, details } = form;
  const { detail } = useContext(RoomsRealtimeContext);
  const { open = false, onClose = () => {} } = props;
  const { t } = useTranslation();
  const dispatchError = useErrorNotification();
  const { resolution, source, comment } = useContext(CloseCaseTicketContext);
  const { setResolution, setSource, setComment } = useContext(
    CloseCaseTicketActionContext
  );
  const { setErrorIssueResolution, setErrorIssueSource } = useContext(
    ErrorIssueActionContext
  );

  useEffect(() => {
    if (ticket.account) {
      (async () => {
        let issueSources = await getIssue(
          ticket.account?.id ?? "",
          "source" as IssueName
        );
        let issueResolutions = await getIssue(
          ticket.account?.id ?? "",
          "resolution" as IssueName
        );
        setSource({
          issueSources,
          issueSource: ticket.issue?.source ?? "",
        });
        setResolution({
          issueResolutions,
          issueResolution: ticket.issue?.resolution ?? "",
        });
      })();
    }
  }, [ticket.account]);

  const onConfirm = useCallback(() => {
    setErrorIssueResolution("");
    setErrorIssueSource("");

    if (resolution.issueResolution !== "" && source?.issueSource !== "") {
      let issue = {
        ...ticket.issue,
        ...{
          resolution: resolution.issueResolution,
          source: source?.issueSource,
          comment: comment,
        },
      };
      dispatchTicket({
        status: "Closed" as TicketStatus,
        issue: issue as TicketIssue,
      });

      const description = t("Closed") + (comment ? ": " : "") + comment;
      const event = {
        subject: ticket?.issue?.issue,
        description: description,
        priority: "Informational" as Priority,
        createDate: new Date().getTime(),
      };
      details?.events?.push(event);
      try {
        (async () => {
          await putTicket({
            ...form.ticket,
            status: "Closed" as TicketStatus,
            issue: issue as TicketIssue,
            details: form.details,
          } as Ticket);
          setComment("");
          dispatch("updateTicket");
          detail?.refetch();
          reset();
          onClose(true);
        })();
      } catch (e) {
        dispatchError(e);
      }
    } else {
      if (resolution.issueResolution === "") {
        setErrorIssueResolution(t("Field is required"));
      }
      if (source.issueSource === "") {
        setErrorIssueSource(t("Field is required"));
      }
    }
  }, [resolution.issueResolution, source?.issueSource, comment, form]);

  const onCancel = () => {
    setErrorIssueResolution("");
    setErrorIssueSource("");
    setComment("");
    onClose();
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={onCancel}>
      <DialogTitle>{t("Close Ticket")}</DialogTitle>
      <DialogContent>
        <CloseCaseContent />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="white" onClick={onConfirm}>
          {t("Close Ticket")}
        </Button>
        <Button variant="outlined" color="greyed" onClick={onCancel}>
          {t("Cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseCaseDialog;
